import type { Awareness } from 'y-protocols/awareness';
import { getClientId, getClientGroupId, CLIENT_PLATFORM } from '@/lib/sync/client-identity';
import { collabColorFromId } from '@/utils/collab-color';

/** The shape of the local user written into Yjs awareness for presence + cursors. */
export interface AwarenessUser {
  id: string;
  name: string;
  color: string;
  avatar: string | null;
  clientId: string;
  clientGroupId: string;
  clientPlatform: string;
}

/** Minimal auth-user fields needed to build the awareness user. */
export interface AuthUserLike {
  sub: string;
  name: string;
  picture?: string | null;
}

/** Build the awareness `user` object from the authenticated user. */
export function buildAwarenessUser(user: AuthUserLike): AwarenessUser {
  return {
    id: user.sub,
    name: user.name,
    color: collabColorFromId(user.sub ?? 'anonymous'),
    avatar: user.picture ?? null,
    clientId: getClientId(),
    clientGroupId: getClientGroupId(),
    clientPlatform: CLIENT_PLATFORM,
  };
}

/**
 * Set the local `user` field on a Yjs awareness instance.
 *
 * Uses `setLocalState({ ...existing, user })` rather than
 * `setLocalStateField('user', user)` because `setLocalStateField` is a SILENT
 * NO-OP when the awareness local state is `null` (a y-protocols footgun). That
 * no-op is the root cause of the intermittent "collaborator cursor doesn't
 * render" bug: a doc subscribed after the presence bridge ran has null local
 * state, and CollaborationCaret's own `setLocalStateField` then fails to
 * register the user. `setLocalState` always writes (creating the state object
 * if needed), so the user is reliably registered regardless of timing.
 */
export function setAwarenessUser(awareness: Awareness, user: AuthUserLike): void {
  const existing = awareness.getLocalState() ?? {};
  awareness.setLocalState({ ...existing, user: buildAwarenessUser(user) });
}
